import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Paper,
  Stack,
  Box,
  Select,
  MenuItem,
  Divider,
  NotFound,
  Typography,
  Tabs,
  Tab
} from '@esgian/esgianui';
import { OperatorsTable } from '@components/Tables';
import { useLookupOperatorsQuery, useSegment } from '@hooks';
import moment from 'moment';
import {
  OperatorCEUChart,
  OperatorFleetAgeChart
} from '@components/Sections/ShipAnalytics/OperatorProfileSection/SubSections/OperatorOverviewSection';
import { fetchVesselStats } from '@api';
import { DATE_FORMAT } from '@constants';
import { TextWithTooltipIcon } from '@components';
import { OperatorMultiSelect } from '@components/Inputs';
import { getOperatorType, getCommercialCategoryType } from '@store/features';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import DateRangePicker from '../../../Inputs/DateRangePicker';
import { OPERATOR_FLEET_DEV_RORO_TITLE, OPERATOR_FLEET_DEV_MPP_TITLE } from '@constants/tooltips';

const ageInfoDefault = {
  averageAge: 0,
  vesselsAgeRange0To5: 0,
  vesselsAgeRange6To10: 0,
  vesselsAgeRange11To15: 0,
  vesselsAgeRange16To20: 0,
  vesselsAgeRange21To25: 0,
  vesselsAgeRangeGreaterThan25: 0
};

function aggregateOperatorData(response, numMonths) {
  let numOperators = response.slice(1).length;
  const operatorTotalVessels = response[0].vesselTimeSeries; //First index has the total values irrespective of operators
  const aggregatedData = {
    totalVessels: operatorTotalVessels.map((item) => item.numVessels || {}),
    operatorAgeInformation: Array.from({ length: numMonths }, () => ({ ...ageInfoDefault })),
    ceuCapacity: response[0].vesselTimeSeries.map(({ capacity }) => ({
      averageCEU: capacity.averageCEU,
      totalCEU: capacity.totalCEU,
      averageDeadweightTonnage: capacity.averageDeadweightTonnage,
      totalDeadweightTonnage: capacity.totalDeadweightTonnage
    })),
    numVesselsArray: Array(numMonths).fill([])
  };

  response.slice(1).forEach((operator) => {
    const operatorFleetData = operator.vesselTimeSeries || [];

    operatorFleetData.forEach((item, index) => {
      aggregatedData.numVesselsArray[index].push(item.numVessels || 0);

      // Aggregate operatorAgeInformation
      const ageInfo = item.ageInformation || {};
      Object.keys(ageInfo).forEach((key) => {
        aggregatedData.operatorAgeInformation[index][key] += ageInfo[key] ?? 0;
      });
    });
  });

  aggregatedData.operatorAgeInformation = aggregatedData.operatorAgeInformation.map((item) => {
    let avg = item.averageAge / numOperators;
    return { ...item, averageAge: avg };
  });
  return aggregatedData;
}

function OperatorsOverviewSection({ overviewData, overviewDataLoading }) {
  const [section, setSection] = useState(1);
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('ageProfile');
  const [fleetDates, setFleetDates] = useState({
    start: moment().subtract(12, 'months').startOf('month'),
    end: moment()
  });
  const [hasSetOperators, setHasSetOperators] = useState(false);

  const lookupOperatorsQuery = useLookupOperatorsQuery();
  const { isRoRo, segment } = useSegment();
  const operatorType = useSelector(getOperatorType);
  const commercialCategoryType = useSelector(getCommercialCategoryType);

  const filterOptions = [
    { value: 'ceuCapacity', label: isRoRo ? 'CEU Capacity' : 'Total Deadweight' },
    { value: 'ageProfile', label: 'Age Profile' }
  ];
  useEffect(() => {
    if (lookupOperatorsQuery.data?.length) {
      if (!hasSetOperators) {
        setSelectedOperators(lookupOperatorsQuery.data);
        setHasSetOperators(true);
      } else {
        setSelectedOperators(lookupOperatorsQuery.data);
      }
    }
  }, [lookupOperatorsQuery.data]);

  const fleetDevelopmentQuery = useQuery({
    queryKey: [
      'fleetDevelopment',
      'operatorsOverview',
      {
        segmentIds: [segment.id],
        operatorIds: selectedOperators.map((operator) => operator.id),
        operatorTypeIds: isRoRo ? [operatorType] : [],
        commercialCategoryIds: isRoRo ? [] : [commercialCategoryType],
        startDate: moment(fleetDates.start).format(DATE_FORMAT),
        endDate: moment(fleetDates.end).format(DATE_FORMAT)
      }
    ],
    enabled: !!fleetDates && !!selectedOperators.length && section === 2,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) => {
      return fetchVesselStats(signal, queryKey[2])
        .then((response) => {
          if (!response.length) return null;
          const start = moment(fleetDates.start);
          const end = moment(fleetDates.end);
          const numMonths = end.diff(start, 'months') + 1;
          const aggregatedData = aggregateOperatorData(response, numMonths);
          const categories = response[0].vesselTimeSeries.map((item) => {
            const date = new Date(item.date);
            return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          });
          return {
            aggregatedData: aggregatedData,
            categories: categories
          };
        })
        .catch(() => {
          return null;
        });
    }
  });

  const { aggregatedData = {}, categories } = fleetDevelopmentQuery.data || {};

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} id={'operators-overview'}>
        <Paper>
          <Tabs
            variant="scrollable"
            onChange={(_, val) => {
              setSection(val);
            }}
            value={section}>
            <Tab value={1} label="Operators Overview" />
            <Tab value={2} label="Fleet Development" />
          </Tabs>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        {section === 1 && (
          <OperatorsTable
            operators={overviewData}
            fileName={'operators-overview'}
            loading={overviewDataLoading}
          />
        )}
        {section === 2 && (
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <TextWithTooltipIcon
                labelVariant={'h6'}
                label={'Fleet Development'}
                tooltipText={isRoRo ? OPERATOR_FLEET_DEV_RORO_TITLE : OPERATOR_FLEET_DEV_MPP_TITLE}
              />
              <Stack direction="row" justifyContent={'space-between'}>
                <Stack direction="row" spacing={2}>
                  <DateRangePicker
                    label={'Period'}
                    isRequired
                    startDate={fleetDates.start}
                    endDate={fleetDates.end}
                    onClose={(start, end) => {
                      setFleetDates({ start: start, end: end });
                    }}
                  />
                  <Box sx={{ width: '300px' }}>
                    <OperatorMultiSelect
                      width={'300px'}
                      helperText={!selectedOperators.length ? 'No operators selected' : ''}
                      error={!selectedOperators.length}
                      placeholder={'Select Operators...'}
                      selectedOperators={selectedOperators}
                      operatorsList={lookupOperatorsQuery.data || []}
                      sx={{ width: '100%' }}
                      handleChange={(operators) => {
                        setSelectedOperators(operators);
                      }}
                    />
                  </Box>
                </Stack>
                <Box sx={{ width: '10em' }} alignSelf={'center'}>
                  <Select
                    value={selectedFilter}
                    onChange={({ target }) => setSelectedFilter(target.value)}
                    displayEmpty
                    variant={'standard'}
                    labelId="operator-overview-filter"
                    inputProps={{ 'aria-label': 'Filter' }}
                    MenuProps={{ PaperProps: { style: { width: '10em' } } }}
                    sx={{ width: '10em' }}>
                    {filterOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Typography variant={'body2'}>{option.label}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Stack>
              <Divider />
              {!fleetDevelopmentQuery.data && !fleetDevelopmentQuery.isFetching ? (
                <NotFound
                  show={true}
                  header={'No Matching Result'}
                  text={'Please change your search parameters'}
                />
              ) : (
                <>
                  {selectedFilter === 'ageProfile' && (
                    <OperatorFleetAgeChart
                      categories={categories}
                      loading={fleetDevelopmentQuery.isFetching}
                      fleetAgeCeuData={aggregatedData.operatorAgeInformation}
                      totalCountVessel={aggregatedData.totalVessels}
                      fileName={`fleet-age`}
                    />
                  )}
                  {selectedFilter === 'ceuCapacity' && (
                    <OperatorCEUChart
                      loading={fleetDevelopmentQuery.isFetching}
                      CEUCapacityData={aggregatedData.ceuCapacity}
                      fileName={`CEU-capacity`}
                      categories={categories}
                      vesselsAdded={aggregatedData.vesselsAdded}
                      vesselsRemoved={aggregatedData.vesselsRemoved}
                    />
                  )}
                </>
              )}
            </Stack>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
}

OperatorsOverviewSection.propTypes = {
  loading: PropTypes.bool,
  overviewData: PropTypes.arrayOf(PropTypes.object),
  overviewDataLoading: PropTypes.bool
};

OperatorsOverviewSection.defaultProps = {};

export default OperatorsOverviewSection;
